import { m } from 'motion/react';
import type { Variants } from 'motion/react';
import { getReducedMotionVariants } from '@/lib/motion';
import { GHOST_SPRITE_CONFIG, ANIMATION_ROWS, AnimationVariant } from '@/lib/sprite-config';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { useEffect, useRef } from 'react';

interface GhostMascotProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: AnimationVariant;
  className?: string;
  showGlow?: boolean;
}

const SIZES = {
  sm: 64,
  md: 96,
  lg: 128,
  xl: 192,
};

export function GhostMascot({
  size = 'md',
  variant = 'calculating', // Default to calculating (with abacus) to match logo
  className = '',
  showGlow = true,
}: GhostMascotProps) {
  const pixelSize = SIZES[size];
  const shouldReduceMotion = useReducedMotion();
  const spriteRef = useRef<HTMLDivElement>(null);

  // Get row for this variant
  const row = ANIMATION_ROWS[variant];

  // Entry animation variants
  const normalContainerVariants: Variants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { type: 'spring', stiffness: 300, damping: 20 },
    },
  };
  const containerVariants = shouldReduceMotion
    ? getReducedMotionVariants(normalContainerVariants)
    : normalContainerVariants;

  // Sprite frame animation
  useEffect(() => {
    if (shouldReduceMotion || !spriteRef.current) return;

    const { cols, fps, frameWidth, frameHeight } = GHOST_SPRITE_CONFIG;
    const frameDuration = 1000 / fps;
    let currentFrame = 0;
    const yOffset = -(row * frameHeight);

    // Set initial position
    spriteRef.current.style.backgroundPosition = `0px ${yOffset}px`;

    const interval = setInterval(() => {
      currentFrame = (currentFrame + 1) % cols;
      if (spriteRef.current) {
        const xOffset = -(currentFrame * frameWidth);
        spriteRef.current.style.backgroundPosition = `${xOffset}px ${yOffset}px`;
      }
    }, frameDuration);

    return () => clearInterval(interval);
  }, [shouldReduceMotion, row]);

  // Scale factor
  const scale = pixelSize / GHOST_SPRITE_CONFIG.frameHeight;
  const yOffset = -(row * GHOST_SPRITE_CONFIG.frameHeight);

  return (
    <m.div
      className={`relative flex items-center justify-center ${className}`}
      style={{ width: pixelSize, height: pixelSize }}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Glow effect */}
      {showGlow && (
        <div
          className="absolute inset-0 rounded-full bg-yellow-400/20 blur-xl animate-pulse-slow"
          style={{ transform: 'scale(1.5)' }}
        />
      )}

      {/* Ghost sprite with floating + frame animation */}
      <m.div
        animate={
          shouldReduceMotion
            ? {}
            : {
                y: [0, -6, 0],
              }
        }
        transition={{
          y: { duration: 2, repeat: Infinity, ease: 'easeInOut' },
        }}
        style={{
          width: pixelSize,
          height: pixelSize,
          overflow: 'hidden',
        }}
      >
        <div
          ref={spriteRef}
          style={{
            width: GHOST_SPRITE_CONFIG.frameWidth,
            height: GHOST_SPRITE_CONFIG.frameHeight,
            backgroundImage: `url(${GHOST_SPRITE_CONFIG.src})`,
            backgroundPosition: `0px ${yOffset}px`,
            backgroundRepeat: 'no-repeat',
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        />
      </m.div>
    </m.div>
  );
}
